/**
 * Brand copy in one place (docs/REDESIGN.md): card names, the nine worlds,
 * player medallions, reaction stickers and the few lines of flavor text.
 * The asset manifest (public/assets/manifest.json) only supplies pictures.
 */
import type { BotLevel } from '../engine';
import type { Emote } from '../worker/protocol';

export const GAME_NAME = 'Ancient Nine';
export const APP_VERSION = '0.2.0';
export const TAGLINE = 'Golf’s oldest world tour.';
export const SHORT_LINE = 'Nine holes. Eight cards. Lowest score wins.';
export const MOTTO = 'FESTINA LENTE';
export const MAX_OPPONENTS = 5;
export const DEFAULT_OPPONENTS = 2;
export const HOLE_CHOICES = [3, 9, 18] as const;

/**
 * Card names. The asset manifest (public/assets/manifest.json, `cards`) is the
 * source of truth: `name` plus `showName`; an empty name or `showName: false`
 * means the card shows its numeral only. This table is only the fallback until
 * the manifest loads (and for the Worker, which has no manifest of its own).
 */
export const CARD_NAMES: Record<number, string> = {
  [-5]: 'Eureka!',
  0: 'Mummy-gan',
  1: 'Veni, Vidi, Putti',
  2: 'Quetzal Birdie',
  3: 'Putt-hagoras',
  4: '',
  5: '',
  6: '',
  7: '',
  8: '',
  9: '',
  10: 'Sphinx Trap',
  11: 'Sunk to Atlantis',
  12: 'Over the Wall',
};

/** Card names from a manifest's `cards` block; '' for a card that shows no name. */
export function manifestCardNames(raw: unknown): Map<number, string> {
  const out = new Map<number, string>();
  const cards = raw && typeof raw === 'object' ? (raw as { cards?: unknown }).cards : undefined;
  if (!cards || typeof cards !== 'object' || Array.isArray(cards)) return out;
  for (const [k, c] of Object.entries(cards as Record<string, unknown>)) {
    const v = Number(k);
    if (!Number.isInteger(v) || v < -5 || v > 12 || !c || typeof c !== 'object') continue;
    const { name, showName } = c as { name?: unknown; showName?: unknown };
    out.set(v, showName === false || typeof name !== 'string' ? '' : name.trim());
  }
  return out;
}

let liveNames = new Map<number, string>();

/** Install the manifest's names (assets.ts calls this once the manifest loads). */
export function setCardNames(names: Map<number, string>): void {
  liveNames = names;
}

/** A card's current name, or '' when it shows its numeral only. */
export function cardNameOf(value: number): string {
  return liveNames.get(value) ?? CARD_NAMES[value] ?? '';
}

/** Values that carry an illustration; 4 to 9 are plain, typeset cards. */
export const ART_VALUES = new Set([-5, 0, 1, 2, 3, 10, 11, 12]);

export const LEVEL_LABEL: Record<BotLevel, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

// ---------------------------------------------------------------------------
// The nine worlds (REDESIGN.md 5)
// ---------------------------------------------------------------------------

export interface World {
  /** 1..9 */
  n: number;
  /** Stable id: the art files and the tuner's saved reviews use it, so it never follows a rename. */
  slug: string;
  /** Plain landmark names a casual player knows at once (Blair, 11-04-2026: no golf words, nothing obscure). */
  name: string;
  place: string;
  flavor: string;
  accent: string;
  /**
   * Tibbs's left and right edges (shell and clubs included) in the phone
   * travel art, as fractions of its width. The portrait crop keeps this span
   * on screen (Travel.tsx, phoneCrop).
   */
  tibbs: readonly [number, number];
}

export const WORLDS: World[] = [
  { n: 1, slug: '01-giza-links', name: 'Giza', place: 'Ancient Egypt', flavor: 'Mind the sand. It goes on for miles.', accent: '#D9A33B', tibbs: [0.02, 0.41] },
  { n: 2, slug: '02-hanging-gardens', name: 'The Hanging Gardens', place: 'Babylon', flavor: 'Very well watered. Bring a towel.', accent: '#2B4C9B', tibbs: [0.4, 0.71] },
  { n: 3, slug: '03-olympia', name: 'Olympia', place: 'Ancient Greece', flavor: 'Laurels for the winner. Lunch for the rest.', accent: '#4F86C6', tibbs: [0.3, 0.71] },
  { n: 4, slug: '04-colosseum', name: 'The Colosseum', place: 'Rome', flavor: 'Fifty thousand seats. Every one of them is watching you.', accent: '#B8502E', tibbs: [0.05, 0.48] },
  { n: 5, slug: '05-gobekli-tepe', name: 'Stonehenge', place: 'Ancient Britain', flavor: 'Nobody knows who built it, or why. The same goes for some swings.', accent: '#8E7B57', tibbs: [0.26, 0.7] },
  { n: 6, slug: '06-long-wall', name: 'The Great Wall', place: 'Ancient China', flavor: 'The longest hole ever built.', accent: '#C0392B', tibbs: [0.44, 0.81] },
  { n: 7, slug: '07-fuji-heights', name: 'Mount Fuji', place: 'Japan', flavor: 'Snow on the summit. Wind off the left.', accent: '#D97A93', tibbs: [0.2, 0.52] },
  { n: 8, slug: '08-voyagers-atoll', name: 'Easter Island', place: 'Polynesia', flavor: 'Steered here by the stars. Every shot breaks toward the sea.', accent: '#1F9AA8', tibbs: [0.25, 0.63] },
  { n: 9, slug: '09-cloud-terraces', name: 'Machu Picchu', place: 'Peru', flavor: 'The final hole. Thin air, long way down.', accent: '#7B4F9E', tibbs: [0.32, 0.69] },
];

/** Which world a hole is played at: 1..9 in order, 18 holes go round twice, playoffs use world 9. */
export function worldFor(hole: number, totalHoles: number): World {
  if (hole > totalHoles) return WORLDS[8]!;
  return WORLDS[(Math.max(1, hole) - 1) % 9]!;
}

/** "3/9", or "P1" for the first playoff hole. */
export function holeCounter(hole: number, totalHoles: number): string {
  return hole > totalHoles ? `P${hole - totalHoles}` : `${hole}/${totalHoles}`;
}

// ---------------------------------------------------------------------------
// Player medallions (REDESIGN.md 7.6): a color and a white glyph per seat
// ---------------------------------------------------------------------------

export const PLAYER_COLORS = ['#2B4C9B', '#B8502E', '#2F8F6E', '#D9A33B', '#4F86C6', '#7B4F9E'] as const;
export const PLAYER_GLYPHS = ['sun', 'wave', 'leaf', 'star', 'moon', 'mountain'] as const;
export type Glyph = (typeof PLAYER_GLYPHS)[number];

// ---------------------------------------------------------------------------
// Reactions: Tibbs stickers (REDESIGN.md 4.3). No text appears with them.
// ---------------------------------------------------------------------------

export const STICKER_LABELS: Record<Emote, string> = {
  'well-played': 'Well played',
  oof: 'Oof',
  smug: 'Smug',
  fore: 'Fore!',
};

/** True minus sign for negative numbers. */
export function fmt(n: number): string {
  return n < 0 ? `−${Math.abs(n)}` : String(n);
}

/** The first word of a long name, for narrow tiles. The full name stays in the accessible label. */
export function tileName(name: string, narrow: boolean): string {
  if (!narrow || name.length <= 9) return name;
  const first = name.split(/\s+/)[0] ?? name;
  return first.length >= 2 ? first : name;
}
